import React, { useContext, useEffect, useMemo, useState } from 'react'
import Button from '../../../button/Button'
import Input from '../../../input/Input'
import { StateContext, DispatchContext } from '../../../../AppContext'
import { IAddress, IState } from 'src/Interface'
import styles from './add-fixed-trip.module.scss'
import { baseApiURL } from 'src/utils/utils'
import axios from 'axios'
import { isValidNumberForRegion } from 'libphonenumber-js'
import AutocompletePoi from '../../../autocomplete/AutocompletePoi'
import DropDown from 'src/components/dropDown/Dropdown'
import { toast } from 'react-toastify'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaFixedTripMapper, gaUserMapper } from 'src/utils/ga/gaMapper'

interface IValues {
  numberOfPeople: number
  destinationAddress: IAddress
  pickupAddress: IAddress
  comment: string
  project: string
  accountId: string
  customerName: string
  customerPhone: string
  price: string
  reference: string
  title: string
}

interface IError {
  [key: string]: boolean
}

interface Props {
  close: () => void
  companyId: number
  branchId: number
}

const AddFixedTrip: React.FunctionComponent<Props> = ({ close, branchId, companyId }: Props) => {
  const dispatch: any = useContext(DispatchContext)
  const [isLoading, setIsLoading] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [errors, setErrors] = useState<IError>({})
  const state: IState = useContext(StateContext)
  const { cancellablePromise } = useCancellablePromise()
  const [validationErrors, setValidationErrors] = useState<any>({})
  const [values, setValues] = useState<IValues>(JSON.parse(window.sessionStorage.getItem('values')) || {
    numberOfPeople: 4,
    destinationAddress: null,
    pickupAddress: null,
    comment: '',
    project: '',
    accountId: '',
    title: '',
    price: '',
    customerName: null,
    customerPhone: '',
    reference: '',
  })
  
  const [bookingModule, setBookingModule] = useState({
    attributes: [],
    fields: [],
    referenceLabel: '',
    updatedAt: ''
  })
  const [selectedAttributes, updateSelectedAttributes] = useState([])

  useEffect(() => {
    if (state.user.accessToken && companyId) {
      cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/booking_module`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          updateSelectedAttributes(result.data.attributesFields.filter(attribute => attribute.required).map(attribute => attribute.id))
          setBookingModule({ attributes: result.data.attributesFields, fields: result.data.staticFields, updatedAt: result.data.updatedAt, referenceLabel: result.data.referenceLabel })
        }).catch((err) => {
          toast(err.response.data.message)
        }))

      cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/accounts`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          setAccounts([{ label: 'ingen konto', value: '' }, ...result.data.filter(item => item.status === "ACTIVE").map(item => ({ label: item.name, value: item.id }))])
        }).catch((err) => {
          toast(err.response.data.message)
        }))
    }
  }, [companyId, state.user.accessToken])


  const handleInputChange = (fieldName: string) => (value: string) => {
    window.sessionStorage.setItem('values', JSON.stringify({
      ...values,
      [fieldName]: value
    }))

    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
    setValidationErrors({
      ...validationErrors,
      [fieldName]: false
    })
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false

    if (fieldName === 'customerPhone') {
      if (!isValidNumberForRegion(value, 'DK')) {
        error = true
      }
    } else {
      if (value && regexp && !regexp.test(value || '')) {
        error = true
      }
    }
    setValidationErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }

  const addTrip = () => {
    if (requiredFieldsLength < requiredFields.length || !values.title) {
      const errors = {};
      requiredFields.filter(code => {
        const fields = [...state.bookingModule.detailFields.list, ...state.bookingModule.customerFields.list]
        const matchedField = fields.find(field => field.code === code)
        return matchedField ? !values[matchedField.name] : false
      }).forEach(code => {
        errors[code] = true
      })
      setErrors({
        ...errors,
        title: !values.title ? true : false
      })
    } else {

      setIsLoading(true)
      axios.post(`${baseApiURL()}/branches/${branchId}/fixed_trips`,
        {
          title: values.title,
          price: values.price ? values.price : null,
          pickupAddress: values.pickupAddress,
          destinationAddress: values.destinationAddress,
          reference: values.reference ? values.reference : null,
          project: values.project ? values.project : null,
          comment: values.comment ? values.comment : null,
          customerName: values.customerName ? values.customerName : null,
          customerPhone: values.customerPhone ? values.customerPhone : null,
          accountId: values.accountId ? values.accountId : null,
          numberOfPeople: values.numberOfPeople,
          attributeIds: selectedAttributes,
          bookingModuleUpdatedAt: bookingModule.updatedAt

        },
        {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
        .then((result) => {
          return result.data.fixedTripsId
        }).then((fixedTripsId) => {
          return axios(`${baseApiURL()}/branches/${branchId}/fixed_trips/${fixedTripsId}`, {
            headers: {
              Authorization:
                `Bearer ${state.user.accessToken}`
            }
          })
        })
        .then((result) => {
          gaEvent(gaEventCategories.fixed_trips, gaEvenActions.created_fixed_trip, gaUserMapper(state), gaFixedTripMapper(values))
          dispatch({ type: 'SET_FIXED_TRIPS', fixedTrips: [result.data, ...state.fixedTrips] })
          setIsLoading(false)
          close()
          toast('Fast tur oprettet')
          window.sessionStorage.removeItem('values')
        }).catch((error) => {
          setIsLoading(false)
           if (error.response.data.message.startsWith('invalid phone number')) {
            setValidationErrors({
              ...validationErrors,
              customerPhone: true
            })
          } 
        })
    }
  }

  const toggleSelectedAddons = (id: any) => {
    if (selectedAttributes.includes(id)) {
      const attributes = [...selectedAttributes]
      const index = selectedAttributes.findIndex(a => a === id)
      attributes.splice(index, 1)
      updateSelectedAttributes(attributes)
    } else {
      updateSelectedAttributes([...selectedAttributes, id])
    }
  }

  //Remove date and time from fixed trip field
  const { detailFields, customerFields, requiredFields } = useMemo(() => {
    const detailFields = bookingModule.fields.filter((item) => item.category === 'DETAILS' && item.code !== 'DA' && item.code !== 'TI')
    const customerFields = bookingModule.fields.filter((item) => item.category === 'CUSTOMER')

    const requiredFields: Array<string> = [...detailFields].filter(field =>
      // Remove following fields from the requiered list
      field.required &&
      field.code !== 'DE' && // remove destination field to avoid duplicates
      field.code !== 'AF' && // remove pickup field to avoid duplicates
      field.code !== 'BE' && // Payment
      field.code !== 'TI' && // Time
      field.code !== 'DA' && // Date
      field.code !== 'FI' // Name
    ).map(field => field.code)

    requiredFields.push('DE', 'AF') // re add Destination & Pickup back to required fields

    return { detailFields, customerFields, requiredFields }
  }, [bookingModule.fields])


  const requiredFieldsLength = requiredFields.filter(code => {
    const fields = [...state.bookingModule.detailFields.list, ...state.bookingModule.customerFields.list]
    const matchedField = fields.find(field => field.code === code)
    return matchedField ? !!values[matchedField.name] : false
  }).length



  return (
    <div className={styles.addCompany}>
      <header className={styles.header}>
        <h4>Opret fast tur</h4>
      </header>
      <div className={styles.container}>
        <div className={styles.section}>
          {state.bookingModule.detailFields.list.filter((propety) => detailFields.find((item) => propety.code === item.code))
            .map((item, index) => {
              switch (item.type) {
                case 'autocomplete':
                  return <div className={`tw-mb-5 ${item.modifier ? item.modifier : ''}`} key={index}>
                    <AutocompletePoi
                      label={`
                    ${item.inputLabel || item.description} *`}
                      placeholder={item.placeholder}
                      iconType={item.name === 'pickupAddress' ? 'pickup' : 'destination'}
                      setAddress={() => handleInputChange(item.name)}
                      completedAddress={values[item.name]}
                      resetError={() => resetError(item.code)}
                      name={item.name} />
                    {errors[item.code] && <p className='errorMsg'>Mangler at udfylde {(item.inputLabel || item.description).toLocaleLowerCase()}</p>}
                  </div>
                case 'dropdownInput':
                  return <DropDown
                    key={index}
                    label={`
                    ${item.inputLabel || item.description}
                     ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                    modifiers={`tw-mb-5 ${item.modifier ? item.modifier : ''} `}
                    curried={true}
                    value={values[item.name]}
                    options={item.dropDownOptions}
                    handleOnChange={() => handleInputChange(item.name)} />
                default:
                  return <Input
                    key={index}
                    placeholder={item.placeholder}
                    modifiers={`tw-mb-3 ${item.modifier ? item.modifier : ''} `}
                    type='text'
                    value={values[item.name]}
                    handleInputChange={() => handleInputChange(item.name)}
                    IconRight={item.IconRight}
                    countryCode={item.countryCode}
                    resetError={() => resetError(item.code)}
                    label={`
                    ${item.inputLabel || item.description}
                     ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                    name={item.name}
                  />
              }
            })}
          {!detailFields.find((item) => 'DE' === item.code) && <AutocompletePoi
            label='Destination *'
            placeholder='Vælg destination'
            iconType='destination'
            modifiers='tw-mb-5 tw-flex-full'
            setAddress={() => handleInputChange('destinationAddress')}
            completedAddress={values.destinationAddress}
            name='destinationAddress'
          />}
          <div className='tw-flex tw-w-full'>
            <div className='tw-flex-3 tw-mr-3'>
              <Input
                placeholder='Titel på fast tur'
                type='text'
                resetError={() => resetError('title')}
                value={values.title}
                handleInputChange={() => handleInputChange('title')}
                label='Titel *'
                name='title' />
              {errors.title && <p className='errorMsg'>Mangler at udfylde title</p>}
            </div>
            <Input
              placeholder='Pris'
              type='text'
              value={values.price}
              handleInputChange={() => handleInputChange('price')}
              currency='kr.'
              modifiers='tw-flex-1 '
              label='Fast pris'
              name='price'
            />
          </div>
        </div>
        {bookingModule.attributes.filter(attribute => !attribute.required).length > 0 && <div className={styles.section}>
          <p className='tw-flex-full'>Tilvalg</p>
          <div className='tw-flex tw-mt-3 tw-flex-wrap'>
            {bookingModule.attributes.filter(attribute => !attribute.required).map((item, index) => <Button modifiers='tw-mr-3 tw-mb-3' handleClick={() => toggleSelectedAddons(item.id)} label={item.description} type={selectedAttributes.includes(item.id) ? 'addonActive' : 'addon'} key={index} />)}
          </div>
        </div>}
        <div className={styles.section}>
          <h6 className='tw-mb-2 tw-flex-full'>Kunde informationer</h6>
          {accounts && accounts.length > 1 && <DropDown value={values.accountId} placeholder='Vælg konto' label='Konto' modifiers='tw-flex-full tw-mb-5' options={accounts} handleOnChange={() => handleInputChange('accountId')} curried={true} />}
          {state.bookingModule.customerFields.list.filter((propety) => customerFields.find((item) => propety.code === item.code))
            .map((item, index) => {
              switch (item.type) {
                case 'dropdown':
                  return <DropDown
                    key={index}
                    label={item.inputLabel || item.description}
                    modifiers={`tw-mb-5 tw-mr-3 ${item.modifier ? item.modifier : ''} `}
                    curried={true}
                    value={values.accountId ? 'card' : 'cash'}
                    options={item.dropDownOptions}
                    handleOnChange={() => handleInputChange(item.name)} />
                default:
                  return <div key={index} > <Input
                    key={index}
                    placeholder={item.placeholder}
                    modifiers={`tw-mb-3 odd:tw-mr-3 ${item.modifier ? item.modifier : ''} `}
                    type='text'
                    isAllowOnlyDigits={item.name ==='customerPhone'}
                    value={values[item.name]}
                    handleValidation={() => handleValidation(item.validation, item.name)}
                    handleInputChange={() => handleInputChange(item.name)}
                    IconRight={item.IconRight}
                    countryCode={item.countryCode}
                    resetError={() => resetError(item.code)}
                    label={`
                    ${item.code === 'RE' ? bookingModule.referenceLabel : item.inputLabel || item.description}`}
                    name={item.name}
                  />
                  {validationErrors.customerPhone && item.name === 'customerPhone' && <p className='errorMsg'>Ugyldigt telefonnummer</p>}
                  </div>
              }
            })}
        </div>
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={close} />
        <Button disabled={(!!Object.values(errors).filter(error => error).length)} loading={isLoading} type='primary' label='Opret fast tur' handleClick={addTrip} />
      </footer>
    </div >
  )
}

export default AddFixedTrip
